import laptopImage from "@assets/images/macbook-air-m2-dark@2x.png";
import { Reveal } from "@components/ui";

interface TimelineCardProps {
  title: string;
  description: string;
  duration: string;
  index: number;
  icon: React.ReactNode;
}

function TimelineCard({ title, description, duration, icon }: TimelineCardProps) {
  return (
    <div className="group relative rounded-[14px] md:rounded-[16px] bg-white flex items-center gap-4 md:gap-5 p-3.5 md:p-4 lg:p-5 hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 border border-transparent hover:border-[#e8e9ed] overflow-hidden">
      {/* Icon */}
      <div className="w-10 h-10 md:w-11 md:h-11 rounded-[12px] bg-[#122d63] flex items-center justify-center text-white shrink-0 group-hover:bg-[#f24d2b] transition-colors duration-300">
        {icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-[15px] md:text-[17px] lg:text-[19px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.2]">
            {title}
          </h3>
          {/* Duration Badge */}
          <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#122d63]/[0.06] text-[11px] md:text-[12px] font-semibold tracking-[-0.02em] text-[#122d63] whitespace-nowrap shrink-0">
            <svg viewBox="0 0 12 12" className="w-3 h-3">
              <circle cx="6" cy="6" r="5" stroke="#122d63" strokeWidth="1" fill="none" />
              <path d="M6 3v3l2 1" stroke="#122d63" strokeWidth="1" strokeLinecap="round" />
            </svg>
            {duration}
          </span>
        </div>
        <p className="text-[12px] md:text-[13px] lg:text-[14px] tracking-[-0.04em] text-[#62636c] font-albert-sans leading-[1.4]">
          {description}
        </p>
        {/* Duration Badge - mobile only */}
        <span className="sm:hidden inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-0.5 rounded-full bg-[#122d63]/[0.06] text-[11px] font-semibold tracking-[-0.02em] text-[#122d63] w-fit">
          <svg viewBox="0 0 12 12" className="w-3 h-3">
            <circle cx="6" cy="6" r="5" stroke="#122d63" strokeWidth="1" fill="none" />
            <path d="M6 3v3l2 1" stroke="#122d63" strokeWidth="1" strokeLinecap="round" />
          </svg>
          {duration}
        </span>
      </div>
    </div>
  );
}

const timelineSteps = [
  {
    title: "Application Review",
    description: "Document verification by customer service",
    duration: "Within 24 hours",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Site Visit / Inspection",
    description: "Safety assessment by engineer",
    duration: "2-3 working days",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Installation & Testing",
    description: "Equipment installation & Civil Defense approval",
    duration: "3-5 working days",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94L6.73 20.2a2 2 0 01-2.83 0l-.1-.1a2 2 0 010-2.83l6.73-6.73A6 6 0 0118.47 2.53L14.7 6.3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Connection Activation",
    description: "Meter setup + account creation",
    duration: "1 working day",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export function ProcessingTimelineSection() {
  return (
    <section className="w-full bg-[#f5f5f7] overflow-hidden py-12 md:py-16 lg:pt-[100px] lg:pb-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col lg:flex-row items-stretch gap-8 lg:gap-10">
        {/* Left Content */}
        <Reveal direction="left" className="flex-1 min-w-0">
          <div className="flex flex-col items-start gap-6 h-full justify-center">
            {/* Title + Subtitle */}
            <div className="flex flex-col items-start gap-4">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
                Timeline
              </span>
              <h2 className="text-[24px] md:text-[36px] lg:text-[48px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
                Quick and Transparent Processing
              </h2>
              <p className="text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] text-[#62636c] font-albert-sans leading-[1.4]">
                Average total completion: 5-10 working days after document
                submission.
              </p>
            </div>

            {/* Timeline Cards */}
            <div className="w-full flex flex-col gap-3">
              {timelineSteps.map((step, index) => (
                <Reveal key={step.title} direction="up" delay={index * 80}>
                  <TimelineCard {...step} index={index} />
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Right - Red Card with Laptop (Desktop Only) */}
        <div className="hidden lg:flex w-[520px] rounded-[24px] overflow-hidden shrink-0 relative bg-[#f24d2b] flex-col items-center">
          <h3 className="pt-16 px-8 text-[40px] tracking-[-0.04em] font-semibold text-white font-albert-sans text-center">
            Start the process in 1min
          </h3>
          <img
            src={laptopImage}
            alt="Laptop showing GASIS application"
            className="mt-8 w-[580px] h-auto z-[2] ml-[40px]"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}
